// A section of records that lives in pipelines (R-U29–R-U31, decision 040): «Мои» → «Все» → filter branches → pipelines → «Новая …».
// The app declares the section with its words and fields; the core gives the column, the pages and the filters. Pure where it can be.

import type { FieldDef, Filter, Id, MetaIndex, TermDef } from '@confdeck/core-shared';

/** A filter branch under «Все»: by where a record stands in its pipeline, by the date of the next action, by the person. */
export type BranchKind = 'start' | 'work' | 'won' | 'lost' | 'overdue' | 'unassigned';
/** A bulk action of the list of «Мои»: move to a stage, give to a person, set the date, close as won. */
export type BulkKind = 'stage' | 'owner' | 'due' | 'won';

export interface PipelineSectionDef {
  typeKey: string;
  /** The app's words: «Мои сделки», «Все сделки», «Новая воронка», «Воронка» (the column and the page), «воронку» (in a question). */
  words: { mine: string; all: string; create: string; pipeline: string; pipelineAcc: string };
  /** Whose records are «мои»: `ownerId` — the record's owner, or the key of a person field (the assignee of a task). */
  mine: string;
  /** The date of the next action: «Мои» group by it (R-U30). */
  due: string;
  /** Fields of a card on the boards besides the title; `<stage>:pipeline` names the pipeline of the stage. */
  card: string[];
  /** Columns of the lists of «Мои» and of a pipeline. */
  list: string[];
  branches?: { key: string; label: string; kind: BranchKind }[];
  /**
   * The follow-up record a stage scenario makes (R-U32, «Задача» of the CRM): the name of the action, its section, the person and date
   * fields, and the relation back to the record.
   */
  followUp?: { label: string; typeKey: string; person: string; due: string; link: string };
  bulk: { kind: BulkKind; label: string }[];
}

const sections = new Map<string, PipelineSectionDef>();
export const registerPipelineSection = (def: PipelineSectionDef) => { sections.set(def.typeKey, def); };
export const pipelineSectionOf = (typeKey: string | undefined) => (typeKey ? sections.get(typeKey) : undefined);

/** The field a section's pipelines work on: the type's required stage-like field over a pipeline taxonomy. */
export function pipelineField(meta: MetaIndex, typeId: Id): FieldDef | undefined {
  const fields = meta.fields(typeId).filter((f) => f.fieldType === 'taxonomy' && !f.archivedAt && meta.taxonomy(f.options.taxonomyId as Id)?.style === 'pipeline');
  return fields.find((f) => f.required) ?? fields[0];
}

/** The pipelines of the field, without the archived ones. */
export const pipelinesOf = (meta: MetaIndex, field: FieldDef): TermDef[] => meta.children(null, field.options.taxonomyId as Id).filter((p) => !p.archivedAt);

/** The stages of one pipeline or of all of them, by where a record stands: the first open stage, the later open ones, the finals. */
export function stagesBy(meta: MetaIndex, field: FieldDef, kind: 'open' | 'start' | 'work' | 'won' | 'lost', pipelineId?: Id): Id[] {
  const roots = pipelineId ? [meta.term(pipelineId)].filter((p): p is TermDef => !!p) : pipelinesOf(meta, field);
  const out: Id[] = [];
  for (const p of roots) {
    const stages = meta.children(p.id, p.taxonomyId);
    const open = stages.filter((t) => !t.final);
    const pick = kind === 'open' ? open : kind === 'start' ? open.slice(0, 1) : kind === 'work' ? open.slice(1) : stages.filter((t) => t.final === kind);
    out.push(...pick.map((t) => t.id));
  }
  return out;
}

const and = (...parts: (Filter | null)[]): Filter | null => {
  const list = parts.filter((p): p is Filter => !!p);
  return list.length === 0 ? null : list.length === 1 ? list[0]! : { and: list };
};

/** Records still in work: on an open stage of any pipeline (or of one). */
export const openFilter = (meta: MetaIndex, field: FieldDef, pipelineId?: Id): Filter => ({ field: field.key, op: 'in', value: stagesBy(meta, field, 'open', pipelineId) });

/** «Мои»: the person's records still in work. */
export const mineFilter = (meta: MetaIndex, def: PipelineSectionDef, field: FieldDef, me: Id | null | undefined): Filter | null =>
  and({ field: def.mine, op: 'eq', value: me ?? '' }, openFilter(meta, field));

/**
 * The filter of a branch under «Все»: «К выполнению» — the first open stage of each pipeline, «В работе» — the later open ones,
 * «Готово» / «Отменена» — the finals; «Просроченные» — still open with the date before today; «Без исполнителя» — still open without
 * the person (предложение: закрытое без исполнителя не мешает работе).
 */
export function branchFilter(meta: MetaIndex, def: PipelineSectionDef, field: FieldDef, kind: BranchKind, today: string): Filter | null {
  switch (kind) {
    case 'start': case 'work': case 'won': case 'lost': return { field: field.key, op: 'in', value: stagesBy(meta, field, kind) };
    case 'overdue': return and(openFilter(meta, field), { field: def.due, op: 'lt', value: today });
    case 'unassigned': return and(openFilter(meta, field), { field: def.mine, op: 'empty' });
  }
}

/** The addresses of the section's pages: «Мои», a pipeline with its tab, and «Все» narrowed to a branch. */
export const mineHref = (typeKey: string, layout?: 'list') => `/m/${typeKey}${layout ? `?layout=${layout}` : ''}`;
export type PipelineTab = 'board' | 'list' | 'gantt' | 'settings';
export const pipelineHref = (id: Id, tab?: PipelineTab) => `/p/${id}${tab && tab !== 'board' ? `?tab=${tab}` : ''}`;

/** The section of records whose pipelines live in the taxonomy of this pipeline (the deals of a pipeline of deals). */
export function pipelineType(meta: MetaIndex, pipelineId: Id): Id | undefined {
  const tax = meta.term(pipelineId)?.taxonomyId;
  return tax ? meta.types().find((t) => pipelineField(meta, t.id)?.options.taxonomyId === tax)?.id : undefined;
}
